import { activeLocale } from "./lib/locale-format.js";
// Peças compartilhadas das telas de chamadas (monitoramento + relatórios).
// Helpers de formatação, badge semântico de status e a tabela reutilizável.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import "./i18n.js";
import {
  Badge,
  Button,
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@organizacaox/lai-design-system";
import { ArrowDownLeft, ArrowUpRight, ExternalLink } from "@organizacaox/lai-design-system/icons";
import { callFailureLabel, type CallListItem, type CallStatus } from "./api.js";
import { useConversionStages } from "@/hooks/use-calls";
import { useDispositions } from "@/hooks/use-dispositions";
import type { Disposition, DispositionCategory } from "@/api/dispositions";
import type { TFunction } from "i18next";
import { CallFormButton } from "./post-call-form.js";
import { CallDetailDialog } from "./call-detail-dialog.js";
import { NoDataIllustration } from "@/components/empty-illustrations";
import { CallRecordingActions } from "./call-recording-actions.js";
import { useAccessSnapshot } from "@/hooks/use-viewer-role";

// Duração em segundos → mm:ss (ou h:mm:ss). "—" quando ausente.
export function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds < 0) return "—";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

// E.164/BR simples: 55DD9XXXXXXXX → (DD) 9XXXX-XXXX. Sem heurística agressiva:
// se não bater o formato esperado, devolve o número cru.
export function formatPhone(raw: string | null): string {
  if (!raw) return "—";
  const digits = raw.replace(/\D/g, "");
  const local = digits.startsWith("55") ? digits.slice(2) : digits;
  if (local.length === 11) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  }
  if (local.length === 10) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  }
  return raw;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString(activeLocale());
}

// Só a hora (hh:mm:ss): para listagens de um único dia, onde a data repetida
// em toda linha é ruído.
export function formatTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString(activeLocale());
}

const SUCCESS_CLASS = "border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-400";
const BAD_CLASS = "border-transparent bg-destructive/15 text-destructive";

// Cores semânticas por status (não só decorativas): completada=verde, em
// andamento/chamando=âmbar/azul, falha=vermelho, não atendida=neutro.
const STATUS_CLASS: Record<CallStatus, string> = {
  COMPLETED: SUCCESS_CLASS,
  IN_PROGRESS: "border-transparent bg-sky-500/15 text-sky-700 dark:text-sky-400",
  RINGING: "border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400",
  FAILED: BAD_CLASS,
  NO_ANSWER: "border-transparent bg-muted text-muted-foreground",
};

const DISPOSITION_CATEGORY_CLASS: Record<DispositionCategory, string> = {
  SUCCESS: SUCCESS_CLASS,
  RETRY: "border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400",
  NO_ANSWER: BAD_CLASS,
  FAILURE: BAD_CLASS,
  DNC: BAD_CLASS,
};

// Tabulação só faz sentido para chamada encerrada — status terminal.
const TERMINAL_STATUSES: ReadonlySet<CallStatus> = new Set(["COMPLETED", "FAILED", "NO_ANSWER"]);

export function StatusBadge({ status }: { status: CallStatus }) {
  const { t } = useTranslation();
  const pulse = status === "RINGING" || status === "IN_PROGRESS";
  return (
    <Badge className={STATUS_CLASS[status]}>
      {pulse && (
        <span className="mr-1.5 inline-block size-1.5 animate-pulse rounded-full bg-current" />
      )}
      {t(`calls.common.status.${status}`)}
    </Badge>
  );
}

// Rótulo do desfecho (o que a coluna Status/Tabulação mostra): a tabulação
// gravada; sem ela, o motivo de falha da telefonia; completada sem motivo vira
// "Ligação realizada". null = nada a dizer (chamada ainda em curso). A tabela e
// o CSV do relatório passam por aqui, então a planilha diz o mesmo que a tela.
export function callOutcomeLabel(
  call: Pick<CallListItem, "dispositionId" | "failureReason" | "status">,
  dispositions: readonly Pick<Disposition, "id" | "label">[] | undefined,
  t: TFunction,
): string | null {
  const disposition = call.dispositionId
    ? dispositions?.find((d) => d.id === call.dispositionId)
    : undefined;
  if (disposition) return disposition.label;
  if (call.failureReason) {
    return t(`calls.common.failureReasons.${call.failureReason}`, {
      defaultValue: callFailureLabel(call.failureReason) ?? call.failureReason,
    });
  }
  return call.status === "COMPLETED" ? t("calls.common.completedDisposition") : null;
}

// Tabulação: só o rótulo gravado. Não há modo de edição — o desfecho é derivado
// da telefonia pela auto-tabulação (back features/call-finalize), nunca escolhido
// à mão.
function DispositionLabel({
  dispositionId,
  failureReason,
  status,
}: {
  dispositionId: string | null;
  failureReason: string | null;
  status: CallStatus;
}) {
  const { t } = useTranslation();
  const { data: dispositions } = useDispositions({ enabled: dispositionId !== null });
  const disposition = dispositionId ? dispositions?.find((d) => d.id === dispositionId) : undefined;
  const label = callOutcomeLabel({ dispositionId, failureReason, status }, dispositions, t);

  // Verde fica só para o que deu certo: com tabulação gravada, manda a categoria
  // do catálogo (caixa postal, não atendeu, insucesso → vermelho; rediscar →
  // âmbar); sem ela, qualquer motivo de falha é vermelho e só a completada sem
  // motivo segue verde.
  const className = disposition?.category
    ? DISPOSITION_CATEGORY_CLASS[disposition.category]
    : failureReason || status === "NO_ANSWER"
      ? BAD_CLASS
      : STATUS_CLASS[status];

  return label ? (
    <Badge className={className}>{label}</Badge>
  ) : (
    <span className="text-muted-foreground">—</span>
  );
}

// Mesma normalização do back (normalizeStage em features/conversions/schema.ts):
// trim + caixa baixa, sem folding de acento. Qualquer outra normalização faz o
// badge divergir do que GET /v1/conversion-stages de fato conta.
function normalizeStage(stage: string): string {
  return stage.trim().toLowerCase();
}

// Etapa ATUAL do lead, classificada contra o mesmo catálogo que decide a taxa
// de conversão. Verde = conta como conversão, vermelho = conta como perda,
// neutro = etapa que ninguém configurou — de propósito, para saltar aos olhos
// na listagem em vez de se camuflar como "tanto faz".
function StageBadge({ leadStage }: { leadStage: string | null }) {
  const { data } = useConversionStages({ enabled: leadStage !== null });
  if (leadStage === null) return <span className="text-muted-foreground">—</span>;
  // Com a config por funil o mesmo rótulo pode estar em mais de uma etapa. A
  // ligação só traz o rótulo do lead, então o selo só toma partido quando
  // todas as etapas com esse rótulo concordam; se um funil diz conversão e
  // outro perda, fica neutro em vez de chutar um lado.
  const resultados = new Set(
    (data?.stages ?? [])
      .filter((s) => normalizeStage(s.stage) === normalizeStage(leadStage))
      .map((s) => s.result),
  );
  const match = resultados.size === 1 ? { result: [...resultados][0] } : undefined;
  const classes =
    match?.result === "CONVERSION"
      ? "border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
      : match?.result === "LOSS"
        ? "border-transparent bg-destructive/15 text-destructive"
        : "border-transparent bg-muted text-muted-foreground";
  return <Badge className={classes}>{leadStage}</Badge>;
}

function DirectionCell({ item }: { item: CallListItem }) {
  const { t } = useTranslation();
  const Icon = item.direction === "INBOUND" ? ArrowDownLeft : ArrowUpRight;
  const number = item.direction === "INBOUND" ? item.callerDid : item.targetPhone;
  // Número discado mascarado por papel (vendedor não vê o número no histórico).
  const outHidden = item.direction === "OUTBOUND" && item.targetPhoneHidden;
  return (
    <div className="flex items-center gap-2">
      <Icon
        className={
          item.direction === "INBOUND"
            ? "size-4 shrink-0 text-emerald-600 dark:text-emerald-400"
            : "size-4 shrink-0 text-sky-600 dark:text-sky-400"
        }
        aria-label={t(`calls.common.direction.${item.direction}`)}
      />
      <span className="font-medium tabular-nums">
        {outHidden ? t("calls.common.hiddenNumber") : formatPhone(number)}
      </span>
    </div>
  );
}

// Tabela reutilizável. `showAgent` liga a coluna Responsável (relatórios da org);
// `showStatus` permite tirar a coluna técnica quando a tabulação já comunica o
// desfecho de forma mais direta;
// `showDuration` liga as duas colunas de tempo — falado e espera — que só fazem
// sentido depois que a chamada terminou (irrelevantes em chamadas ativas);
// `showCallForm` decide se a coluna Tabulação traz o botão do formulário
// pós-ligação ao lado do desfecho (monitoramento) ou só o desfecho (relatórios);
// `showStage` liga a coluna Etapa atual — só a Visão geral liga (é onde a taxa
// de conversão por virada de etapa vive), nenhuma outra tela deve ligar;
// `showHubspot` liga a coluna com o botão que abre o lead no HubSpot;
// `timeOnly` mostra só a hora no Início — para quando o recorte é um dia só.
export function CallsTable({
  recordingMenu = false,
  calls,
  showAgent = true,
  showStatus = true,
  showDuration = true,
  showDisposition = true,
  showCallForm = true,
  showStage = false,
  showHubspot = false,
  timeOnly = false,
  emptyLabel,
}: {
  calls: CallListItem[];
  showAgent?: boolean;
  showStatus?: boolean;
  showDuration?: boolean;
  showDisposition?: boolean;
  showCallForm?: boolean;
  showStage?: boolean;
  showHubspot?: boolean;
  timeOnly?: boolean;
  recordingMenu?: boolean;
  emptyLabel?: string;
}) {
  const { t } = useTranslation();
  const { permissions } = useAccessSnapshot();
  const canDeleteRecording = permissions.has("reports.calls.removeRecording");
  const resolvedEmptyLabel = emptyLabel ?? t("calls.common.empty");
  // showDuration vale por DUAS colunas (tempo falado + espera). Áudio é uma
  // coluna fixa: é onde a pessoa baixa ou exclui só a gravação.
  const colCount =
    4 +
    (showAgent ? 1 : 0) +
    (showStatus ? 1 : 0) +
    (showDuration ? 2 : 0) +
    (showDisposition ? 1 : 0) +
    (showStage ? 1 : 0) +
    (showHubspot ? 1 : 0);
  // Ligação aberta no diálogo de detalhe (clique na linha).
  const [selected, setSelected] = useState<CallListItem | null>(null);
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("calls.common.columns.number")}</TableHead>
            <TableHead>{t("calls.common.columns.lead")}</TableHead>
            {showAgent && <TableHead>{t("calls.common.columns.owner")}</TableHead>}
            {showStatus && <TableHead>{t("calls.common.columns.status")}</TableHead>}
            {/* Sem a coluna técnica de status, a tabulação É o status da ligação. */}
            {showDisposition && (
              <TableHead>
                {t(showStatus ? "calls.common.columns.disposition" : "calls.common.columns.status")}
              </TableHead>
            )}
            {showStage && <TableHead>{t("calls.common.columns.currentStage")}</TableHead>}
            {showDuration && (
              <TableHead className="text-right">{t("calls.common.columns.talkTime")}</TableHead>
            )}
            {showDuration && (
              <TableHead className="text-right">{t("calls.common.columns.wait")}</TableHead>
            )}
            <TableHead className="text-right">{t("calls.common.columns.recording")}</TableHead>
            <TableHead className="text-right">{t("calls.common.columns.startedAt")}</TableHead>
            {showHubspot && (
              <TableHead className="text-right">{t("calls.common.columns.hubspot")}</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {calls.length === 0 && (
            <TableRow>
              <TableCell colSpan={colCount}>
                <Empty>
                  <EmptyHeader>
                    <EmptyMedia>
                      <NoDataIllustration />
                    </EmptyMedia>
                    <EmptyTitle>{resolvedEmptyLabel}</EmptyTitle>
                  </EmptyHeader>
                </Empty>
              </TableCell>
            </TableRow>
          )}
          {calls.map((c) => (
            <TableRow
              key={c.id}
              tabIndex={0}
              onClick={() => setSelected(c)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setSelected(c);
                }
              }}
              className="cursor-pointer focus-visible:bg-muted/60 focus-visible:outline-none"
            >
              <TableCell>
                <DirectionCell item={c} />
              </TableCell>
              <TableCell className="text-sm">
                {c.leadName ? (
                  // title: o nome inteiro fica acessível quando a coluna trunca.
                  <span className="block max-w-[16rem] truncate" title={c.leadName}>
                    {c.leadName}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              {showAgent && (
                <TableCell className="text-sm">
                  {c.agentName ?? <span className="text-muted-foreground">—</span>}
                </TableCell>
              )}
              {showStatus && (
                <TableCell>
                  <StatusBadge status={c.status} />
                </TableCell>
              )}
              {showDisposition && (
                <TableCell>
                  <div className="flex items-center gap-2">
                    <DispositionLabel
                      dispositionId={c.dispositionId}
                      failureReason={c.failureReason}
                      status={c.status}
                    />
                    {showCallForm && TERMINAL_STATUSES.has(c.status) && (
                      <span onClick={(e) => e.stopPropagation()}>
                        <CallFormButton callId={c.id} />
                      </span>
                    )}
                  </div>
                </TableCell>
              )}
              {showStage && (
                <TableCell>
                  <StageBadge leadStage={c.leadStage} />
                </TableCell>
              )}
              {showDuration && (
                <TableCell className="text-right tabular-nums text-sm">
                  {formatDuration(c.duration)}
                </TableCell>
              )}
              {showDuration && (
                <TableCell className="text-right tabular-nums text-sm text-muted-foreground">
                  {formatDuration(c.waitSeconds)}
                </TableCell>
              )}
              <TableCell className="text-right">
                {c.recordingName ? (
                  <CallRecordingActions
                    callId={c.id}
                    canDelete={canDeleteRecording}
                    menu={recordingMenu}
                  />
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </TableCell>
              <TableCell className="text-right text-sm text-muted-foreground tabular-nums">
                {(timeOnly ? formatTime : formatDateTime)(c.startedAt ?? c.createdAt)}
              </TableCell>
              {showHubspot && (
                <TableCell className="text-right">
                  {c.hubspotUrl ? (
                    // O clique no link não pode abrir o detalhe da linha.
                    <Button
                      render={<a href={c.hubspotUrl} target="_blank" rel="noopener noreferrer" />}
                      nativeButton={false}
                      role="link"
                      variant="outline"
                      size="sm"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="size-4" />
                      {t("calls.common.openHubspot")}
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <CallDetailDialog
        call={selected}
        open={selected !== null}
        onOpenChange={(v) => !v && setSelected(null)}
        canDeleteRecording={canDeleteRecording}
      />
    </div>
  );
}
